import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  ValidationFilters,
  ResolutionRequest,
  ScrapeOptions,
} from "../types/validation";
import * as validationApi from "../services/validationApi";

// Query keys factory
export const validationKeys = {
  all: ["validation"] as const,
  issues: (filters: ValidationFilters) =>
    [...validationKeys.all, "issues", filters] as const,
  issuesByUrl: (url: string, params: { limit?: number; offset?: number }) =>
    [...validationKeys.all, "issuesByUrl", url, params] as const,
  urlsSummary: (params: { source?: string; limit?: number; offset?: number }) =>
    [...validationKeys.all, "urlsSummary", params] as const,
  stats: (filters: ValidationFilters) =>
    [...validationKeys.all, "stats", filters] as const,
  productIssues: (
    productId: string,
    params: { include_variants?: boolean; limit?: number; offset?: number },
  ) => [...validationKeys.all, "productIssues", productId, params] as const,
};

export function useIssues(filters: ValidationFilters = {}) {
  return useQuery({
    queryKey: validationKeys.issues(filters),
    queryFn: () => validationApi.listIssues(filters),
  });
}

export function useIssuesByUrl(
  url: string,
  params: { limit?: number; offset?: number } = {},
) {
  return useQuery({
    queryKey: validationKeys.issuesByUrl(url, params),
    queryFn: () => validationApi.getIssuesByUrl(url, params),
    enabled: !!url,
  });
}

export function useUrlsSummary(
  params: { source?: string; limit?: number; offset?: number } = {},
) {
  return useQuery({
    queryKey: validationKeys.urlsSummary(params),
    queryFn: () => validationApi.getUrlsSummary(params),
  });
}

export function useStats(filters: ValidationFilters = {}) {
  return useQuery({
    queryKey: validationKeys.stats(filters),
    queryFn: () => validationApi.getStats(filters),
  });
}

export function useIssuesCount(
  filters: { source?: string; status?: string } = {},
) {
  return useQuery({
    queryKey: ["validation", "issues", "count", filters] as const,
    queryFn: () => validationApi.getIssuesCount(filters),
  });
}

export function useProductIssues(
  productId: string,
  params: { include_variants?: boolean; limit?: number; offset?: number } = {},
) {
  return useQuery({
    queryKey: validationKeys.productIssues(productId, params),
    queryFn: () => validationApi.getProductIssues(productId, params),
    enabled: !!productId,
  });
}

export function useAddressIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      issueId,
      resolution,
    }: {
      issueId: number;
      resolution: ResolutionRequest;
    }) => validationApi.addressIssue(issueId, 0, "", resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: validationKeys.all });
    },
  });
}

export function useBulkAddressIssues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      issues,
      resolution,
    }: {
      issues: Array<{ id: number }>;
      resolution: ResolutionRequest;
    }) => {
      for (const issue of issues) {
        await validationApi.addressIssue(issue.id, 0, "", resolution);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: validationKeys.all });
    },
  });
}

/** New API does not support unaddressing; mutation no-ops for compatibility. */
export function useUnaddressIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_: {
      logId: string;
      issueIndex: number;
      issueType: string;
    }) => {
      await validationApi.unaddressIssue("", 0, "");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: validationKeys.all });
    },
  });
}

export function useStartScrape() {
  return useMutation({
    mutationFn: ({
      source,
      options,
    }: {
      source: string;
      options?: ScrapeOptions;
    }) => validationApi.startScrape(source, options),
  });
}

export function useScrapeUrl() {
  return useMutation({
    mutationFn: ({ url, options }: { url: string; options?: ScrapeOptions }) =>
      validationApi.scrapeUrl(url, options),
  });
}
